import {getCookie} from '@wandb/weave/common/util/cookie';
import {urlPrefixed} from '@wandb/weave/config';
import {HTTPError} from '@wandb/weave/errors';

// =============================================================================
// Provider Configuration
// =============================================================================

export const LlmProxyProviderConfig = {
  'api.openai.com': {
    endpoints: ['v1/chat/completions', 'v1/responses'] as const,
  },
  'api.anthropic.com': {
    endpoints: ['v1/messages'] as const,
  },
} as const;

export type LlmProxyProviderHost = keyof typeof LlmProxyProviderConfig;

export type LlmProxyEndpointForHost<THost extends LlmProxyProviderHost> =
  (typeof LlmProxyProviderConfig)[THost]['endpoints'][number];

// =============================================================================
// Types
// =============================================================================

export interface LlmProxyClientConfig {
  baseUrl: string;
  entity: string;
  project: string;
}

interface NetworkRequestOptions {
  signal?: AbortSignal;
}

interface LlmProxyResponse<T> {
  data: T;
  status: number;
  headers: Headers;
}

interface LlmProxyAvailabilityResponse {
  available: boolean;
}

// =============================================================================
// Errors
// =============================================================================

type RateLimitErrorCode = 'request_limit_exceeded' | 'usage_limit_exceeded';

interface RateLimitErrorBody {
  code: RateLimitErrorCode;
  message: string;
  retry_after_seconds: number;
}

interface RateLimitResponse {
  error: RateLimitErrorBody;
}

export class LlmProxyRateLimitError extends Error {
  public readonly code: RateLimitErrorCode;
  public readonly retryAfterSeconds: number;

  constructor(
    message: string,
    code: RateLimitErrorCode,
    retryAfterSeconds: number
  ) {
    super(message);
    this.name = 'LlmProxyRateLimitError';
    this.code = code;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

// =============================================================================
// Client
// =============================================================================

export class LlmProxyClient {
  private readonly baseUrl: string;
  private readonly entity: string;
  private readonly project: string;

  constructor(config: LlmProxyClientConfig) {
    this.baseUrl = config.baseUrl.replace(/\/+$/, '');
    this.entity = config.entity;
    this.project = config.project;
  }

  async proxy<
    TRequest,
    TResponse,
    THost extends LlmProxyProviderHost = LlmProxyProviderHost,
  >(
    targetHost: THost,
    targetPath: LlmProxyEndpointForHost<THost>,
    targetBody: TRequest,
    attributes?: Record<string, unknown>,
    targetHeaders?: Record<string, string>,
    options?: NetworkRequestOptions
  ): Promise<LlmProxyResponse<TResponse>> {
    const url = this.buildUrl(targetHost, targetPath);
    const headers = this.buildHeaders(attributes, targetHeaders);

    const response = await this.fetchProxyResponse(
      url,
      headers,
      targetBody,
      options
    );

    const data = (await response.json()) as TResponse;
    return {
      data,
      status: response.status,
      headers: response.headers,
    };
  }

  async *proxyStream<
    TRequest,
    THost extends LlmProxyProviderHost = LlmProxyProviderHost,
  >(
    targetHost: THost,
    targetPath: LlmProxyEndpointForHost<THost>,
    targetBody: TRequest,
    attributes?: Record<string, unknown>,
    targetHeaders?: Record<string, string>,
    options?: NetworkRequestOptions
  ): AsyncGenerator<unknown> {
    const url = this.buildUrl(targetHost, targetPath);
    const headers = this.buildHeaders(attributes, targetHeaders, true);

    const response = await this.fetchProxyResponse(
      url,
      headers,
      targetBody,
      options
    );

    if (!response.body) {
      return;
    }

    yield* this.processSSEStream(response.body);
  }

  async checkAvailability(
    targetUrl: string,
    options?: NetworkRequestOptions
  ): Promise<LlmProxyAvailabilityResponse> {
    const encodedTarget = encodeURIComponent(targetUrl);
    const url = `${this.baseUrl}/availability?target_url=${encodedTarget}`;
    const headers = this.buildAttributionHeaders();

    try {
      // eslint-disable-next-line wandb/no-unprefixed-urls
      const response = await fetch(urlPrefixed(url), {
        method: 'GET',
        credentials: 'include',
        headers,
        signal: options?.signal,
      });

      if (!response.ok) {
        return {available: false};
      }

      return (await response.json()) as LlmProxyAvailabilityResponse;
    } catch {
      return {available: false};
    }
  }

  // ---------------------------------------------------------------------------
  // Private: URL building
  // ---------------------------------------------------------------------------

  private buildUrl(targetHost: string, targetPath: string): string {
    const cleanPath = targetPath.startsWith('/')
      ? targetPath.slice(1)
      : targetPath;
    const target = cleanPath ? `${targetHost}/${cleanPath}` : targetHost;
    return `${this.baseUrl}/proxy/${target}`;
  }

  // ---------------------------------------------------------------------------
  // Private: Headers
  // ---------------------------------------------------------------------------

  private buildHeaders(
    attributes?: Record<string, unknown>,
    targetHeaders?: Record<string, string>,
    streaming?: boolean
  ): Record<string, string> {
    const headers: Record<string, string> = {
      ...targetHeaders,
      ...this.buildAttributionHeaders(),
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };

    if (streaming) {
      headers.Accept = 'text/event-stream';
    }

    if (attributes) {
      headers['x-wandb-attributes'] = JSON.stringify(attributes);
    }

    return headers;
  }

  private buildAttributionHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      Authorization: 'Basic ' + btoa(':'),
      'x-wandb-entity': this.entity,
      'x-wandb-project': this.project,
    };
    if (getCookie('use_admin_privileges') === 'true') {
      headers['use-admin-privileges'] = 'true';
    }
    return headers;
  }

  // ---------------------------------------------------------------------------
  // Private: HTTP
  // ---------------------------------------------------------------------------

  private async fetchProxyResponse(
    url: string,
    headers: Record<string, string>,
    body: unknown,
    options?: NetworkRequestOptions
  ): Promise<Response> {
    // eslint-disable-next-line wandb/no-unprefixed-urls
    const response = await fetch(urlPrefixed(url), {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify(body),
      signal: options?.signal,
    });

    if (response.status === 429) {
      await this.handleRateLimitError(response);
    }

    if (!response.ok) {
      throw new HTTPError(
        response.statusText,
        response.status,
        await response.text()
      );
    }

    return response;
  }

  private async handleRateLimitError(response: Response): Promise<never> {
    if (response.status !== 429) {
      throw new Error(
        `handleRateLimitError called with non-429 status: ${response.status}`
      );
    }

    let rateLimitResponse: RateLimitResponse | undefined;

    try {
      rateLimitResponse = (await response.json()) as RateLimitResponse;
    } catch {}

    const code = rateLimitResponse?.error?.code ?? 'request_limit_exceeded';
    const message =
      rateLimitResponse?.error?.message ??
      'Rate limit exceeded. Please try again later.';
    const retryAfter =
      rateLimitResponse?.error?.retry_after_seconds ??
      parseInt(response.headers.get('Retry-After') ?? '60', 10);

    throw new LlmProxyRateLimitError(message, code, retryAfter);
  }

  // ---------------------------------------------------------------------------
  // Private: SSE streaming
  // ---------------------------------------------------------------------------

  private async *processSSEStream(
    stream: ReadableStream<Uint8Array>
  ): AsyncGenerator<unknown> {
    const reader = stream.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const {done, value} = await reader.read();

        if (done) {
          if (buffer.trim()) {
            const chunk = this.parseSSELine(buffer);
            if (chunk !== null) {
              yield chunk;
            }
          }
          break;
        }

        buffer += decoder.decode(value, {stream: true});

        let newlineIndex;
        while ((newlineIndex = buffer.indexOf('\n')) >= 0) {
          const line = buffer.slice(0, newlineIndex);
          buffer = buffer.slice(newlineIndex + 1);

          const chunk = this.parseSSELine(line);
          if (chunk !== null) {
            yield chunk;
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  }

  private parseSSELine(line: string): unknown | null {
    const trimmed = line.trim();

    if (!trimmed || !trimmed.startsWith('data:')) {
      return null;
    }

    const data = trimmed.slice(5).trim();

    if (data === '[DONE]') {
      return null;
    }

    try {
      return JSON.parse(data);
    } catch (err) {
      console.warn('Failed to parse SSE data:', data, err);
      return null;
    }
  }
}
